import React, { MouseEvent, ReactElement, forwardRef, useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';

import {
  Comp,
  EventKey,
  FocusWithinGroupActionType,
  FocusWithinGroupContext,
  Icon,
  ListboxActionType,
  ListboxContext,
  isComponent,
  useTheme,
} from '@redsift/design-system';
import { SelectTriggerProps } from './types';
import { useMergeRefs, usePopoverContext } from '@redsift/popovers';
import { SelectContext } from '../select/context';
import { mdiChevronDown } from '@redsift/icons';

const COMPONENT_NAME = 'SelectTrigger';
const CLASSNAME = 'redsift-select-trigger';

/**
 * The SelectTrigger component.
 */
export const SelectTrigger: Comp<SelectTriggerProps, HTMLButtonElement> = forwardRef((props, ref) => {
  const { children, hideExpandButton, ...otherProps } = props;

  const { getReferenceProps, isOpen, handleOpen, refs } = usePopoverContext();
  const childrenRef = (children as any).ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);

  const focusContext = useContext(FocusWithinGroupContext);
  const selectContext = useContext(SelectContext);
  const listboxState = useContext(ListboxContext);
  const theme = useTheme();

  const renderedChildren =
    typeof children === 'function' ? children({ value: selectContext?.value, isOpen }) : children;

  const _inputRef = useRef<HTMLInputElement>();
  const inputRef = renderedChildren.props.inputRef ?? _inputRef;

  const handleKeyDown = (event: KeyboardEvent) => {
    const code = event.code;

    if (code === 'Escape') {
      if (isOpen) {
        handleOpen(false);
      }
    } else if (code === 'ArrowDown') {
      if (!isOpen) {
        handleOpen(true);
      }
      if (focusContext.state.selectedId !== null) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.ArrowDown,
              ctrlKey: event.ctrlKey,
            },
          },
        });
      } else if (!focusContext.state.activedescendant?.[0]) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.Home,
              ctrlKey: event.ctrlKey,
            },
          },
        });
      } else {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.FOCUS_ON_LIST,
            payload: {
              id: focusContext.state.activedescendant?.[0] ?? '',
            },
          },
        });
      }
      event.preventDefault();
    } else if (code === 'ArrowUp') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowUp : EventKey.Home,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
    } else if (code === 'Home') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.Home,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
    } else if (code === 'End') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.End,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
    } else if (code === 'Enter') {
      if (isOpen && focusContext.state.selectedId) {
        listboxState.dispatch({
          type: ListboxActionType.SET,
          payload: {
            values: [focusContext.state.activedescendant?.[1] ?? ''],
            activedescendant: focusContext.state.activedescendant,
          },
        });
      }
    }
  };

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    if (isOpen) {
      handleOpen(false);
    } else {
      handleOpen(true);
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.FOCUS_ON_LIST,
          payload: {
            id: focusContext.state.activedescendant?.[0] ?? '',
          },
        },
      });
    }
  };

  useEffect(() => {
    selectContext?.setValue(listboxState.state.selectedValues[0]);
    handleOpen(false);
  }, [listboxState.state]);

  if (isComponent('TextField')(renderedChildren)) {
    return React.cloneElement(renderedChildren, {
      ...getReferenceProps({
        ref: triggerRef,
        ...otherProps,
        ...(renderedChildren as ReactElement).props,
        children: (renderedChildren as ReactElement).props.children ?? '',
      }),
      'aria-activedescendant': isOpen ? focusContext.state.activedescendant?.[0] : undefined,
      className: classNames((renderedChildren as ReactElement).props.className, selectContext?.triggerClassName),
      color: selectContext?.color ?? (renderedChildren as ReactElement).props.color,
      inputRef,
      internal: (value?: string, isDisabled?: boolean, isInvalid?: boolean, isRequired?: boolean) => {
        return (
          <>
            {typeof (renderedChildren as ReactElement).props.internal === 'function'
              ? (renderedChildren as ReactElement).props.internal(value, isDisabled, isInvalid, isRequired)
              : (renderedChildren as ReactElement).props.internal}
            {hideExpandButton ? null : (
              <Icon
                color={isDisabled ? 'no-data' : 'question'}
                icon={mdiChevronDown}
                style={{
                  transition: 'transform 300ms ease-out',
                  transform: `rotate(${isOpen ? '-180deg' : '0'})`,
                  cursor: 'pointer',
                }}
              />
            )}
          </>
        );
      },
      isDisabled: selectContext?.isDisabled,
      isReadOnly: true,
      theme,
      role: 'combobox',
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      style: { ...(renderedChildren as ReactElement).props.style, caretColor: 'transparent', cursor: 'pointer' },
      variant: selectContext?.variant ?? (renderedChildren as ReactElement).props.variant,
      ...(selectContext?.isInvalid === true && { color: 'error' }),
      ...(typeof children !== 'function' &&
        selectContext?.value && {
          value: selectContext.value,
        }),
      inputProps: {
        ...(renderedChildren as ReactElement).props.inputProps,
        style: { ...(renderedChildren as ReactElement).props.inputProps?.style, cursor: 'pointer' },
      },
    });
  } else if (isComponent('Button')(renderedChildren)) {
    const children = (renderedChildren as ReactElement).props.children;
    const label = typeof children === 'string' ? children : children?.textContent;

    return React.cloneElement(renderedChildren, {
      ...getReferenceProps({
        ref: triggerRef,
        ...props,
        ...(renderedChildren as ReactElement).props,
        children: (renderedChildren as ReactElement).props.children ?? '',
      }),
      'aria-activedescendant': isOpen ? focusContext.state.activedescendant?.[0] : undefined,
      'aria-label': (renderedChildren as ReactElement).props['aria-label'] ?? label,
      className: classNames((renderedChildren as ReactElement).props.className, selectContext?.triggerClassName),
      color: selectContext?.color ?? (renderedChildren as ReactElement).props.color,
      isActive: isOpen,
      isDisabled: selectContext?.isDisabled,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      rightIcon: (renderedChildren as ReactElement).props.rightIcon ?? mdiChevronDown,
      rightIconProps: (renderedChildren as ReactElement).props.rightIcon
        ? (renderedChildren as ReactElement).props.rightIconProps
        : (renderedChildren as ReactElement).props.rightIconProps ?? {
            style: {
              transition: 'transform 300ms ease-out',
              transform: `rotate(${isOpen ? '-180deg' : '0'})`,
            },
          },
      role: 'combobox',
      theme,
      variant: selectContext?.variant ?? (renderedChildren as ReactElement).props.variant,
      ...(selectContext?.isInvalid === true && { color: 'error' }),
      ...(typeof children !== 'function' &&
        selectContext?.value && {
          children: selectContext.value,
        }),
    });
  }

  return <>{renderedChildren}</>;
});
SelectTrigger.className = CLASSNAME;
SelectTrigger.displayName = COMPONENT_NAME;
